import React, { useEffect, useRef, useState } from 'react';
import { FRAGMENT_COLORS } from '../../constants/sequence';
import { FONT_FAMILY } from '../../constants/typography';

interface FragmentPanelButtonProps {
  type: 'loop' | 'alt' | 'opt' | 'par';
  /** Called when dropped on the canvas. screenX/screenY are relative to the canvas element. */
  onDropOnCanvas: (type: string, screenX: number, screenY: number) => void;
}

export const FragmentPanelButton: React.FC<FragmentPanelButtonProps> = ({ type, onDropOnCanvas }) => {
  const [isDragging, setIsDragging] = useState(false);
  const hasMoved = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const color = FRAGMENT_COLORS[type] ?? '#6366f1';
  const label = type.charAt(0).toUpperCase() + type.slice(1);

  const getOrCreateGhost = () => {
    let ghost = document.getElementById('frag-drag-ghost') as HTMLDivElement | null;
    if (!ghost) {
      ghost = document.createElement('div');
      ghost.id = 'frag-drag-ghost';
      Object.assign(ghost.style, {
        position: 'fixed',
        zIndex: '9999',
        pointerEvents: 'none',
        width: '160px',
        height: '28px',
        backgroundColor: color,
        borderRadius: '4px 0 4px 0',
        opacity: '0.88',
        boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 0 1px ${color}60`,
        display: 'flex',
        alignItems: 'center',
        padding: '0 10px',
        gap: '8px',
        fontFamily: FONT_FAMILY,
      });

      const typeEl = document.createElement('span');
      typeEl.style.cssText = `font-size:10px;font-weight:700;color:white;text-transform:uppercase;letter-spacing:0.08em;`;
      typeEl.textContent = type.toUpperCase();

      const lineEl = document.createElement('div');
      lineEl.style.cssText = `flex:1;height:1px;background:rgba(255,255,255,0.4);border-top:1px dashed rgba(255,255,255,0.5);`;

      ghost.appendChild(typeEl);
      ghost.appendChild(lineEl);
      document.body.appendChild(ghost);
    }
    return ghost;
  };

  const removeGhost = () => {
    const ghost = document.getElementById('frag-drag-ghost');
    if (ghost) document.body.removeChild(ghost);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    startPos.current = { x: e.clientX, y: e.clientY };
    hasMoved.current = false;
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - startPos.current.x;
      const dy = e.clientY - startPos.current.y;
      if (!hasMoved.current && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        hasMoved.current = true;
      }
      if (!hasMoved.current) return;
      const ghost = getOrCreateGhost();
      ghost.style.left = `${e.clientX - 80}px`; // center horizontally
      ghost.style.top  = `${e.clientY - 14}px`; // center vertically
    };

    const onMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      removeGhost();
      if (!hasMoved.current) return;

      const canvasEl = document.querySelector('[data-canvas-area]');
      if (!canvasEl) return;
      const rect = canvasEl.getBoundingClientRect();
      const dropX = e.clientX - rect.left;
      const dropY = e.clientY - rect.top;
      if (dropX >= 0 && dropY >= 0 && dropX <= rect.width && dropY <= rect.height) {
        onDropOnCanvas(type, dropX, dropY);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, type, onDropOnCanvas]);

  useEffect(() => () => removeGhost(), []);

  return (
    <button
      onMouseDown={handleMouseDown}
      className="px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors hover:bg-white/10 flex items-center gap-1.5"
      style={{
        color,
        border: `1px solid ${isDragging && hasMoved.current ? color + '60' : 'var(--panel-border, #252d40)'}`,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        background: isDragging && hasMoved.current ? `${color}15` : undefined,
      }}
      title={`Drag to place ${label} block, or click to add at bottom`}
    >
      <span style={{ opacity: 0.7, fontSize: 10 }}>⠿</span>
      {label}
    </button>
  );
};
